import type Luminary from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';

import type { LintFinding } from '../videoLinter';

import { partitionTranscriptByScene } from 'js/src/agent/tools/transcript-partition';
import { isVoiceDrivenSceneGroup } from 'js/src/luminary/luminary-selectors';
import { getVoicedrivenSceneDuration } from 'js/src/luminary/voicedriven-scenes';

/**
 * Past this an overlay holds one visual across several beats of speech — the coarse-chunk failure
 * where a whole paragraph is dropped on a single scene.
 */
const LONG_OVERLAY_MS = 5000;

/** A one-sentence beat is roughly this many spoken words; well past it the overlay covers several. */
const MAX_OVERLAY_WORDS = 15;

const seconds = (ms: number): string => (ms / 1000).toFixed(1);

/**
 * Warning: an overlay scene in a voice-driven scene group covers too much of the narration to hold
 * on one frame — over ~5s, or more than a single sentence (~15 words). A word-bound overlay's on-
 * screen time is the narration it covers, so a paragraph-sized range keeps one visual up across
 * several beats, which is the coarse-chunking that produces over-long "mega" scenes.
 *
 * Only overlay scenes are judged. A voice-driven group's base scene — the talking-head speaker, or
 * a voiceover coverage gap — is not an overlay, so a video the user asked to keep as a single
 * continuous shot is never flagged here.
 *
 * Warning only — this gives the build's review-and-fix loop a reason to keep splitting rather than
 * blocking a deliberately long moment.
 */
export function checkVoicedrivenSceneTooLong(
    luminary: Luminary,
    getState: GetRootState,
): LintFinding[] {
    const findings: LintFinding[] = [];
    const state = getState();

    luminary.sceneGroups.forEach((sceneGroup) => {
        if (!isVoiceDrivenSceneGroup(sceneGroup)) {
            return;
        }

        // Partition the group's transcript once, not once per scene: the partition reads the
        // group's whole Lexical editor, so a per-scene call would re-read it on every overlay and
        // on every pass of the review-and-fix loop. A `null` result means the group has no
        // transcript at all, which `voice_group_missing_transcript` already reports as an error.
        const partitions = partitionTranscriptByScene(state, sceneGroup);
        if (!partitions) {
            return;
        }

        partitions.forEach(({ scene, role, tokens }) => {
            if (role !== 'overlay') {
                return;
            }
            const spokenWords = tokens.filter((token) => !token.isPause).length;
            if (spokenWords === 0) {
                return;
            }

            const duration = getVoicedrivenSceneDuration(scene);
            if (duration <= LONG_OVERLAY_MS && spokenWords <= MAX_OVERLAY_WORDS) {
                return;
            }

            findings.push({
                check: 'voicedriven_scene_too_long',
                severity: 'warning',
                sceneId: scene.id,
                message:
                    `Overlay covers ${seconds(duration)}s and ${spokenWords} words of ` +
                    `narration. Recommend covering fewer words per overlay.`,
            });
        });
    });

    return findings;
}
